import { useTranslation } from 'react-i18next';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { GlassCard } from '@/components/glass';
import type { WeeklyActivityItem, ReportTypeMeta } from '../types';

const COLORS = [
  'var(--color-primary-800)',
  '#0097a7',
  '#e65100',
  '#7b1fa2',
  '#2e7d32',
  '#c62828',
];

interface WeeklyActivityChartProps {
  data: WeeklyActivityItem[];
  reportTypes?: ReportTypeMeta[];
}

export function WeeklyActivityChart({ data, reportTypes }: WeeklyActivityChartProps) {
  const { t, i18n } = useTranslation();
  const isHe = i18n.language === 'he';

  // Derive report type codes from data
  const codes = reportTypes?.map(rt => rt.code)
    || (data.length > 0 ? Object.keys(data[0]).filter(k => k !== 'week') : []);

  const nameMap = new Map(reportTypes?.map(rt => [rt.code, isHe ? rt.name : rt.nameEn]) || []);

  const chartData = data.map((item) => ({
    ...item,
    name: `${t('dashboard.week')} ${item.week}`,
  }));

  return (
    <GlassCard title={t('dashboard.weeklyActivity')} padding="compact" contentClassName="h-48">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
          />
          <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {codes.map((code, i) => (
            <Bar
              key={code}
              dataKey={code}
              name={nameMap.get(code) || code}
              fill={COLORS[i % COLORS.length]}
              radius={[4, 4, 0, 0]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </GlassCard>
  );
}
